// useFanClub — the hand-written per-domain hooks over the generated client for the Antenna
// fan-club settings modal (shot-42): the club name rename, the Stripe payouts status + onboarding link,
// and the tier editor. Lives under app/hooks/ (the lint-exempt wrapper dir), built on useBandQuery/useBandMutation
// so every key is ['band', bandId, 'antenna', …] and a module can never fetch outside its tenant.
//
// Mutation posture: the two PUTs are confirm-then-repaint — NEVER optimistic. Both carry the settings
// version as `If-Match: "<version>"` (the house optimistic-concurrency header, cf. useTasks.useTransitionTask) so
// a stale write returns 409 rather than silently clobbering a change made in another tab; the 409 surfaces
// verbatim. UpdateSettingsRequest/UpdateClubRequest have no version FIELD, so the version rides the
// header only — see the reconcile note in the report.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";
import { settingsKey } from "./useAnnouncements.js";

type FanClubSettingsView = components["schemas"]["FanClubSettingsView"];
type FanClubTier = components["schemas"]["FanClubTier"];
type PayoutStatusView = components["schemas"]["PayoutStatusView"];
type OnboardLinkView = components["schemas"]["OnboardLinkView"];

// ---- query keys ---------------------------------------------------------------------------------------

/** The fan-club settings key the settings modal reads (GET /antenna/club → name + tiers + version). */
export const clubKey = (bandId: string) => ["band", bandId, "antenna", "club"] as const;

/** The Stripe payout-status key (GET /antenna/payouts). */
export const payoutsKey = (bandId: string) => ["band", bandId, "antenna", "payouts"] as const;

// ---- queries ------------------------------------------------------------------------------------------

/**
 * GET the fan-club settings the modal edits — club name, tiers, and the concurrency `version`.
 * `antenna:manage`; a read-only seat 403s → the query's error state (the client mirror of the server deny).
 */
export function useClubSettings() {
  const { api } = useApp();
  return useBandQuery<FanClubSettingsView>(
    ["antenna", "club"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/antenna/club",
        { params: { path: { bandId } } },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { staleTime: 60_000 }, // settings rarely change (directory-class freshness)
  );
}

/**
 * GET the Stripe payout/connect status — whether onboarding is complete and payouts are live.
 * `antenna:manage`. Short client cache so a return-from-Stripe tab flip reflects the fresh state.
 */
export function usePayoutStatus() {
  const { api } = useApp();
  return useBandQuery<PayoutStatusView>(
    ["antenna", "payouts"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/antenna/payouts",
        { params: { path: { bandId } } },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { staleTime: 15_000 },
  );
}

// ---- mutations (confirm-then-repaint) -----------------------------------------------------------------

/** Seed the freshly returned view into the club + settings caches, then converge via an invalidate refetch. */
function cacheSettings(
  qc: ReturnType<typeof useQueryClient>,
  bandId: string,
  view: FanClubSettingsView,
) {
  qc.setQueryData(clubKey(bandId), view);
  qc.setQueryData(settingsKey(bandId), view); // the composer's audience selector reads this key
  void qc.invalidateQueries({ queryKey: clubKey(bandId) });
  void qc.invalidateQueries({ queryKey: settingsKey(bandId) });
}

/**
 * Rename the fan club (PUT /antenna/settings → 200; 400 invalid / 409 stale). Not optimistic. Carries
 * the current `version` as `If-Match` so a concurrent change surfaces 409 verbatim rather than being clobbered.
 */
export function useUpdateFanClubSettings() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<FanClubSettingsView, { clubName: string | null; version: number }>(
    async (band, { clubName, version }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/antenna/settings",
        {
          params: { path: { bandId: band } },
          headers: { "If-Match": `"${version}"` },
          body: { clubName },
        },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: (view) => cacheSettings(qc, bandId, view) },
  );
}

/**
 * Save the tier ladder (PUT /antenna/club → 200; 400 invalid / **409 tier-has-members**). Not
 * optimistic. Removing a paid tier that still has members is a 409 whose ProblemDetails surfaces verbatim —
 * the modal must NOT silently retry. Carries `version` as `If-Match` (same concurrency guard as the rename).
 */
export function useUpdateClubTiers() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<FanClubSettingsView, { tiers: FanClubTier[]; version: number }>(
    async (band, { tiers, version }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/antenna/club",
        {
          params: { path: { bandId: band } },
          headers: { "If-Match": `"${version}"` },
          body: { tiers },
        },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: (view) => cacheSettings(qc, bandId, view) },
  );
}

/**
 * Start Stripe onboarding (POST /antenna/payouts/onboard → an Account Link). Returns the one-time URL
 * the modal opens; the payout status is invalidated so a return trip repaints the row. Not optimistic.
 */
export function useStartPayoutOnboarding() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<OnboardLinkView, void>(
    async (band) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/antenna/payouts/onboard",
        { params: { path: { bandId: band } } },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: payoutsKey(bandId) }) },
  );
}
